import type { LetterSnapshot } from './letter-snapshot'

export enum EstimateBalanceWorkflowStatus {
  DRAFT = 'DRAFT',
  CONFIRMED = 'CONFIRMED',
  NA = 'NA',
  NOT_READY = 'NOT_READY',
  NOT_STARTED = 'NOT_STARTED',
}

export enum EstimateBalancePaymentStatus {
  UNPAID = 'UNPAID',
  PARTIAL = 'PARTIAL',
  PAID = 'PAID',
  OVERPAID = 'OVERPAID',
  REFUNDED = 'REFUNDED',
}

export interface EstimateBalanceSubItem {
  visitNumber: number
  estimateVisitId: string
  appointmentId: string | null
  total: number
  patientResp: number
  insuranceResp: number
  paid: number
  refunded: number
  /** Distinct CPT codes for the visit. Absent on pre-contract rows → consumers fetch separately. */
  cptCodes?: string[]
  /** Per-visit CPT discount, stored negative. Present only when a discount was applied. */
  cptDiscountApplied?: number
  /** Per-visit charity care discount amount. Present only when charity care was applied. */
  charityCareDiscountApplied?: number
}

/**
 * Active Stripe SubscriptionSchedule driving the plan. Stripe owns cadence/retries/dunning;
 * PIA stores only what it needs to render UI and route webhook events back to this balance.
 */
export interface BalancePaymentPlan {
  stripeSubscriptionScheduleId: string
  /** Per-installment amount for months 2..N (the Stripe schedule itself). */
  monthlyAmount: number
  /** Total installments INCLUDING the upfront charge. The schedule runs for `months - 1` iterations. */
  months: number
  /** Upfront charge on plan creation. Defaults to `monthlyAmount`; absent on pre-contract rows. */
  firstPayment?: number
  // Serialized as ISO string over HTTP; consumers needing a Date normalize at the parse site.
  startedAt: Date | string
}

export interface EstimateBalance {
  id: string
  /** Human-friendly identifier surfaced in UI and on documents. Generated on create; uppercase. */
  shortId: string
  clinicId: string
  estimateId: string
  patientId: string
  workflowStatus: EstimateBalanceWorkflowStatus
  paymentStatus: EstimateBalancePaymentStatus
  totalAmount: number
  patientResponsibility: number
  insuranceResponsibility: number
  paidAmount: number
  refundedAmount: number
  subItems: EstimateBalanceSubItem[]
  /** Idempotency keys (e.g. Stripe `event.id`) so a retried webhook cannot double-credit. */
  appliedSources: string[]
  /** Append-only history of letter PDFs produced for this balance. Each Send writes a fresh element; prior elements are never modified. */
  letterSnapshots: LetterSnapshot[]
  /** Set when a Stripe Subscription Schedule is driving installments; cleared on schedule completion or cancellation. */
  paymentPlan?: BalancePaymentPlan | null
  finalizedAt: Date | string | null
  createdAt: Date | string
  updatedAt: Date | string
}
